<template>
  <v-card class="pa-6">
    <overlay :value="loading"></overlay>
    <v-card-title class="text-uppercase">
      <v-row class="my-1 justify-space-between align-center">
        <div>Traducciones — {{ serviceName }}</div>
      </v-row>
    </v-card-title>
    <hr class="my-3" />
    <v-card-text class="my-0 py-0">
      <v-row class="my-1">
        <v-col cols="12">
          <v-text-field
            label="Título original (Español)"
            :value="serviceName"
            outlined
            dense
            readonly
            disabled
            hide-details="auto"
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row v-for="t in translations" :key="t.id" class="my-1" align="center">
        <v-col cols="8">
          <v-text-field
            :label="languageLabel(t.language)"
            v-model="t.title"
            outlined
            dense
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col cols="4" class="d-flex">
          <v-btn color="primary" rounded small :loading="t.saving" @click="saveExisting(t)">
            Guardar
          </v-btn>
          <v-btn text color="pink" rounded small class="ml-2" @click="triggerDeleteModal(t)">
            Eliminar
          </v-btn>
        </v-col>
      </v-row>

      <template v-if="availableLanguages.length">
        <hr class="my-3" />
        <v-row class="my-1" align="center">
          <v-col cols="4">
            <v-select
              v-model="newLanguage"
              :items="availableLanguages"
              :item-text="languageLabel"
              item-value="code"
              label="Agregar idioma"
              outlined
              dense
              hide-details="auto"
              clearable
            ></v-select>
          </v-col>
          <v-col v-if="newLanguage" cols="8">
            <v-row align="center">
              <v-col cols="8">
                <v-text-field
                  v-model="newTitle"
                  :label="`Título en ${languageLabel(newLanguage)}`"
                  outlined
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-btn color="primary" rounded small :loading="savingNew" @click="saveNew">
                  Agregar
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
      <div v-else-if="!loading" class="text-center grey--text pa-6">
        Ya existe una traducción para todos los idiomas configurados.
      </div>
    </v-card-text>
    <hr class="my-3" />
    <v-card-actions class="justify-end">
      <v-btn text color="primary" rounded @click="$emit('close-dialog')">Cerrar</v-btn>
    </v-card-actions>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card class="pa-6">
        <v-card-title class="text-uppercase">
          <div>Eliminar traducción</div>
        </v-card-title>
        <hr class="my-3" />
        <v-card-text class="my-0 py-0">
          <p>¿Está seguro de que desea eliminar esta traducción?</p>
        </v-card-text>
        <hr class="my-3" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="closeDeleteDialog">Cancelar</v-btn>
          <v-btn color="primary" rounded @click="deleteTranslation(translationPendingDelete)">
            Sí, eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-card>
</template>

<script>
import Overlay from '../components/Overlay.vue';
import ServiceTranslationsService from '../services/service-translations.service';
import { utilsMixin } from '../mixins/utilsMixin';

// Display-only labels for known language codes. An unmapped code (e.g. a
// brand-new country's language before anyone updates this map) still works —
// it just falls back to showing the raw uppercase code instead of a name.
const LANGUAGE_LABELS = {
  es: 'Español',
  en: 'English',
  pt: 'Português'
};

// The original service title always covers Spanish — it is never a
// translation row, so 'es' never appears as addable or as an existing row.
const ORIGINAL_LANGUAGE = 'es';

export default {
  name: 'ServiceTranslationsDialog',
  components: { Overlay },
  mixins: [utilsMixin],
  props: {
    serviceId: { type: String, default: null },
    serviceName: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      languages: [],
      translations: [],
      newLanguage: null,
      newTitle: '',
      savingNew: false,
      deleteDialog: false,
      translationPendingDelete: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'red'
    };
  },
  computed: {
    availableLanguages() {
      const existingCodes = this.translations.map(t => t.language);
      return this.languages.filter(
        code => code !== ORIGINAL_LANGUAGE && !existingCodes.includes(code)
      );
    }
  },
  watch: {
    serviceId: {
      immediate: true,
      handler(val) {
        if (val) this.loadData();
      }
    }
  },
  methods: {
    languageLabel(code) {
      return LANGUAGE_LABELS[code] || (code || '').toUpperCase();
    },
    async loadData() {
      this.loading = true;
      const [languagesRes, translationsRes] = await Promise.all([
        ServiceTranslationsService.getLanguages(),
        ServiceTranslationsService.getByService(this.serviceId)
      ]);
      if (languagesRes.status === 'ok') {
        this.languages = languagesRes.data;
      } else {
        this.showErrorSnackbar('Error al cargar los idiomas disponibles');
      }
      if (translationsRes.status === 'ok') {
        this.translations = translationsRes.data
          .filter(t => t.language !== ORIGINAL_LANGUAGE)
          .map(t => ({ ...t, saving: false }));
      } else {
        this.showErrorSnackbar('Error al cargar las traducciones del servicio');
      }
      this.newLanguage = null;
      this.newTitle = '';
      this.loading = false;
    },
    async saveExisting(translation) {
      if (!translation.title || !translation.title.trim()) {
        this.showErrorSnackbar('El título no puede estar vacío');
        return;
      }
      translation.saving = true;
      const res = await ServiceTranslationsService.update(translation.id, {
        title: translation.title
      });
      translation.saving = false;
      if (res.status === 'ok') {
        this.showSuccessSnackbar(`Traducción al ${this.languageLabel(translation.language)} guardada correctamente`);
      } else {
        this.showErrorSnackbar(res.message || 'Ocurrió un error guardando la traducción, intenta más tarde');
      }
    },
    async saveNew() {
      if (!this.newLanguage) return;
      if (!this.newTitle || !this.newTitle.trim()) {
        this.showErrorSnackbar('El título no puede estar vacío');
        return;
      }
      this.savingNew = true;
      const res = await ServiceTranslationsService.create({
        serviceId: this.serviceId,
        language: this.newLanguage,
        title: this.newTitle
      });
      this.savingNew = false;
      if (res.status === 'ok') {
        this.translations.push({ ...res.data, saving: false });
        this.showSuccessSnackbar(`Traducción al ${this.languageLabel(this.newLanguage)} agregada correctamente`);
        this.newLanguage = null;
        this.newTitle = '';
      } else {
        this.showErrorSnackbar(res.message || 'Ocurrió un error guardando la traducción, intenta más tarde');
      }
    },
    triggerDeleteModal(translation) {
      this.translationPendingDelete = translation;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.translationPendingDelete = null;
    },
    async deleteTranslation(translation) {
      if (!translation) return;
      const res = await ServiceTranslationsService.remove(translation.id);
      if (res.status === 'ok') {
        this.translations = this.translations.filter(t => t.id !== translation.id);
        this.showSuccessSnackbar('Traducción eliminada correctamente');
      } else {
        this.showErrorSnackbar(res.message || 'No se pudo eliminar la traducción');
      }
      this.closeDeleteDialog();
    }
  }
};
</script>
